import React from 'react'
import { FinancialIntegration } from '@ecountabl/lib'
import FormField from '../form-field'
import { REACT_APP_SERVER_ROOT } from '../config'
import BannoRedirectUrlBaseField from './banno-redirect-url-base-field'

/**
 * Build an absolute http(s) URL under REACT_APP_SERVER_ROOT only.
 * Avoids interpreting untrusted integration fields as URL schemes or hosts (CodeQL: DOM text as HTML).
 */
function trustedSameOriginApiUrl(pathFromServerRoot: string): string | null {
  const segment = pathFromServerRoot.replace(/^\//, '')
  try {
    const baseStr = REACT_APP_SERVER_ROOT.replace(/\/?$/, '/')
    const base = new URL(baseStr)
    if (base.protocol !== 'http:' && base.protocol !== 'https:') return null
    const resolved = new URL(segment, base)
    if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') return null
    if (resolved.origin !== base.origin) return null
    return resolved.href
  } catch {
    return null
  }
}

type Props = {
  integration: FinancialIntegration & { isModified?: boolean }
  onChange: (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  onDelete: () => void
}

const platformOptions: Array<{ value: FinancialIntegration['platform']; label: string }> = [
  { value: 'finastra', label: 'Finastra' },
  { value: 'banno', label: 'Banno / Jack Henry (Auth Framework)' },
]

export const IntegrationForm = ({ integration, onChange, onDelete }: Props) => {
  const platform = (integration.platform || 'finastra') as FinancialIntegration['platform']

  const renderFinastra = () => {
    const { finastraTenantId, finastraClientId, finastraClientSecret, isModified } = integration as any
    const canLogin = finastraTenantId && finastraClientId && finastraClientSecret
    const finastraAuthHref = trustedSameOriginApiUrl(
      `api/oauth/finastra/oidc/${encodeURIComponent(String(finastraTenantId))}`
    )

    const fields = [
      { field: 'finastraTenantId', label: 'Finastra Tenant ID', type: 'text' },
      { field: 'finastraClientId', label: 'Finastra App Client ID', type: 'text', autocomplete: 'off' as const },
      { field: 'finastraClientSecret', label: 'Finastra App Client Secret', type: 'password', autocomplete: 'off' as const },
      { field: 'finastraB2BTenant', label: 'Finastra B2B Tenant', type: 'text', autocomplete: 'off' as const },
      { field: 'finastraB2BClientId', label: 'Finastra B2B Client ID', type: 'text', autocomplete: 'off' as const },
      { field: 'finastraB2BClientSecret', label: 'Finastra B2B Client Secret', type: 'password', autocomplete: 'off' as const },
    ]

    return (
      <>
        {fields.map((f) => (
          <FormField value={(integration as any)[f.field] || ''} key={f.field} onChange={onChange as any} {...f} />
        ))}

        <div style={{ display: 'flex', marginTop: 'auto', paddingTop: '0.25rem' }}>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={onDelete}
            aria-label="Remove integration"
          >
            <i className="fa fa-trash" aria-hidden />
          </button>
          <div style={{ marginLeft: '0.5em', display: 'inline-block' }}>
            {canLogin ? (
              isModified ? (
                <div style={{ fontSize: '0.8em', maxWidth: '240px', display: 'inline-block' }}>
                  Changes must be saved before you can login via Finastra
                </div>
              ) : finastraAuthHref ? (
                <a
                  className="btn btn-link btn-sm"
                  target="_blank"
                  rel="noreferrer"
                  href={finastraAuthHref}
                >
                  Login via Finastra <i className="fa fa-external-link"></i>
                </a>
              ) : (
                <span className="btn btn-link btn-sm disabled" style={{ pointerEvents: 'none', opacity: 0.6 }}>
                  Login via Finastra (invalid URL)
                </span>
              )
            ) : null}
          </div>
        </div>
      </>
    )
  }

  const renderBanno = () => {
    const fields = [
      {
        field: 'bannoEnvironment',
        label: 'Banno Environment (e.g. https://digital.garden-fi.com)',
        type: 'text',
        autocomplete: 'off' as const,
      },
      { field: 'bannoClientId', label: 'Banno External App Client ID', type: 'text', autocomplete: 'off' as const },
      { field: 'bannoClientSecret', label: 'Banno External App Client Secret', type: 'password', autocomplete: 'off' as const },
    ]

    return (
      <>
        {fields.map((f) => (
          <FormField value={(integration as any)[f.field] || ''} key={f.field} onChange={onChange as any} {...f} />
        ))}

        <BannoRedirectUrlBaseField
          value={(integration as any).bannoRedirectUrlBase || ''}
          onChange={onChange as any}
        />

        <div style={{ display: 'flex', marginTop: 'auto', paddingTop: '0.25rem' }}>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={onDelete}
            aria-label="Remove integration"
          >
            <i className="fa fa-trash" aria-hidden />
          </button>
        </div>
      </>
    )
  }

  return (
    <div className="finastra-form" style={{ border: '1px solid #eee', padding: '1em', borderRadius: 10 }}>
      <FormField
        value={(integration as any).integrationId || ''}
        onChange={onChange as any}
        label="Integration ID"
        type="text"
        field="integrationId"
      />

      <div className="form-group">
        <label>Integration Platform</label>
        <select className="form-control" name="platform" value={platform as any} onChange={onChange}>
          {platformOptions.map((opt) => (
            <option key={opt.value as any} value={opt.value as any}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {platform === 'finastra' ? renderFinastra() : null}
      {platform === 'banno' ? renderBanno() : null}
    </div>
  )
}

export default IntegrationForm

